import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, CheckCircle2, Info } from "lucide-react";
import { ConexionAPI, ROL_META } from "../../lib/conexion";
import { useAuth } from "../../context/AuthContext";
import UbicacionSelector from "../../components/UbicacionSelector";

// Tipos de usuario canónicos de ConstruLink Red (sin duplicados legacy).
const TIPOS_USUARIO = ["cliente", "contratista", "ferreteria"];

// Normaliza roles legacy (maestro/profesional) al canónico. Otros (p.ej. admin)
// se conservan tal cual para no alterarlos al guardar.
function normalizarRol(r) {
  if (r === "maestro" || r === "profesional") return "contratista";
  return r || "cliente";
}

export default function ConexionPerfil() {
  const { refresh } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    ConexionAPI.obtenerPerfil().then((p) => {
      setPerfil({
        nombre: p.nombre || "",
        rol: normalizarRol(p.rol),
        telefono: p.telefono || "",
        descripcion: p.descripcion || "",
        ubic: {
          region: p.ubicacion?.region || "",
          provincia: p.ubicacion?.provincia || "",
          distrito: p.ubicacion?.distrito || "",
        },
        email: p.email,
      });
    });
  }, []);

  if (!perfil) return <div className="text-center py-16 text-slate-500 text-sm">Cargando…</div>;

  function set(field, value) {
    setPerfil((p) => ({ ...p, [field]: value }));
  }

  async function guardar(e) {
    e.preventDefault();
    setSaving(true);
    setOk(false);
    setError("");
    try {
      const ubicOk = perfil.ubic.region && perfil.ubic.provincia && perfil.ubic.distrito;
      await ConexionAPI.actualizarPerfil({
        nombre: perfil.nombre,
        rol: perfil.rol,
        telefono: perfil.telefono,
        descripcion: perfil.descripcion,
        ubicacion: ubicOk ? perfil.ubic : undefined,
      });
      await refresh();
      setOk(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar el perfil.");
    } finally {
      setSaving(false);
      setTimeout(() => setOk(false), 3000);
    }
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="conexion-perfil">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">Mi perfil de Conexión</p>
        <h2 className="mt-1 text-2xl font-extrabold text-slate-900 tracking-tight">Completa tu perfil profesional</h2>
        <p className="text-sm text-slate-500 mt-1">Datos usados para tus publicaciones y contactos en ConstruLink Red.</p>

        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-brand-blue/20 bg-brand-blue/[0.04] px-4 py-3" data-testid="conexion-perfil-aviso-global">
          <Info size={16} className="mt-0.5 shrink-0 text-brand-blue" />
          <p className="text-xs text-slate-600 leading-relaxed">
            Estos datos provienen de tu{" "}
            <Link to="/mi-perfil/global" className="font-semibold text-brand-blue hover:underline" data-testid="conexion-perfil-link-global">Perfil Global</Link>
            {" "}— edítalos ahí para mantener todo sincronizado en el Ecosistema.
          </p>
        </div>

        <form onSubmit={guardar} className="mt-6 space-y-4" data-testid="conexion-perfil-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nombre completo</label>
              <input
                value={perfil.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                data-testid="conexion-perfil-nombre"
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Correo (no editable)</label>
              <input
                disabled
                value={perfil.email}
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Tipo de Usuario *</label>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {TIPOS_USUARIO.map((id) => {
                const m = ROL_META[id];
                const active = perfil.rol === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => set("rol", id)}
                    data-testid={`conexion-perfil-rol-${id}`}
                    className={[
                      "px-3 py-2.5 rounded-xl text-sm font-semibold border transition-all text-left",
                      active ? "text-white border-transparent shadow-sm" : "text-slate-600 bg-white border-slate-200 hover:bg-slate-50",
                    ].join(" ")}
                    style={active ? { backgroundColor: m.color } : {}}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Teléfono</label>
            <input
              value={perfil.telefono}
              onChange={(e) => set("telefono", e.target.value)}
              data-testid="conexion-perfil-telefono"
              placeholder="Ej. 987654321"
              className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Descripción</label>
            <textarea
              rows={3}
              value={perfil.descripcion}
              onChange={(e) => set("descripcion", e.target.value)}
              data-testid="conexion-perfil-descripcion"
              placeholder="Cuéntanos brevemente a qué te dedicas…"
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none resize-none"
            />
          </div>

          <UbicacionSelector
            value={perfil.ubic}
            onChange={(v) => set("ubic", v)}
            testIdPrefix="conexion-perfil"
            showTotals
          />

          {error && (
            <p className="text-sm text-brand-red bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="conexion-perfil-guardar"
              className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              <Save size={16} /> {saving ? "Guardando…" : "Guardar cambios"}
            </button>
            {ok && (
              <span className="inline-flex items-center gap-1.5 text-green-600 text-sm font-semibold">
                <CheckCircle2 size={16} /> Perfil actualizado
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
